import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { formatDuration } from '../lib/utils';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  ChevronRight, 
  Clock,
  CalendarIcon,
  Play,
  Search,
  TrendingUp,
  TrendingDown,
  FileText,
  Table2,
  CalendarDays
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfDay, endOfDay, addDays, addWeeks, subWeeks, addMonths, subMonths, isSameDay, isToday, isBefore, eachDayOfInterval, getHours, getMinutes, differenceInMinutes, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

const HOURS_START = 6; // 6 AM
const HOURS_END = 22; // 10 PM
const HOUR_HEIGHT = 60; // pixels per hour

const CalendarView = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const { startTimer } = useTimer();
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // View state
  const [viewMode, setViewMode] = useState('week'); // 'month', 'week', 'day'
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Modal state
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    description: '',
    project_id: '',
    date: new Date(),
    start_time: '09:00',
    end_time: '17:00'
  });
  
  // Tooltip state
  const [hoveredEntry, setHoveredEntry] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipTimeoutRef = useRef(null);
  
  // Drag state for resizing
  const [dragState, setDragState] = useState(null);
  const [showDateChangeConfirm, setShowDateChangeConfirm] = useState(false);
  const [pendingDateChange, setPendingDateChange] = useState(null);
  
  // Project search
  const [projectSearch, setProjectSearch] = useState('');
  
  // Last week data for comparison
  const [lastWeekEntries, setLastWeekEntries] = useState([]);
  
  // Leave requests and holidays
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  
  // Summary data from API (includes both timer + manual timesheet entries)
  const [weeklySummary, setWeeklySummary] = useState(null);

  const calendarRef = useRef(null);
  const timelineRef = useRef(null);

  // Calculate date range based on view
  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return { start, end };
    } else if (viewMode === 'month') {
      return { start: startOfMonth(currentDate), end: endOfMonth(currentDate) };
    } else {
      return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
    }
  }, [viewMode, currentDate]);

  // Fetch data when date range changes
  useEffect(() => {
    fetchData();
  }, [dateRange, api]);

  // Scroll to 9AM on load (improvement #9)
  useEffect(() => {
    if (timelineRef.current && (viewMode === 'week' || viewMode === 'day')) {
      // Scroll to 9am instead of 8am
      const workingDayStart = 9; // 9 AM
      const scrollPosition = (workingDayStart - HOURS_START) * HOUR_HEIGHT - 20; // 20px padding
      timelineRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, [viewMode]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigatePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, currentDate]);

  const fetchData = async () => {
    if (!api) return;
    try {
      setLoading(true);
      
      // Fetch entries for current period, plus summary API for accurate totals
      const [entriesRes, projectsRes, summaryRes] = await Promise.all([
        api.get('/time-entries', {
          params: {
            start_date: dateRange.start.toISOString(),
            end_date: dateRange.end.toISOString()
          }
        }),
        api.get('/projects'),
        // Fetch summary data which merges timer + manual timesheet entries
        viewMode === 'week' ? api.get('/reports/summary?period=week') : Promise.resolve({ data: null })
      ]);
      
      setEntries(entriesRes.data);
      setProjects(projectsRes.data);
      
      // Set summary data for accurate weekly total (includes manual timesheet entries)
      if (summaryRes.data) {
        setWeeklySummary(summaryRes.data);
      }
      
      // Fetch last week's entries for comparison (only in week view)
      if (viewMode === 'week') {
        const lastWeekStart = subWeeks(dateRange.start, 1);
        const lastWeekEnd = subWeeks(dateRange.end, 1);
        const lastWeekRes = await api.get('/time-entries', {
          params: {
            start_date: lastWeekStart.toISOString(),
            end_date: lastWeekEnd.toISOString()
          }
        });
        setLastWeekEntries(lastWeekRes.data);
      }
      
      // Fetch leave requests for calendar display
      try {
        const leaveRes = await api.get('/time-off/my-requests');
        setLeaveRequests(leaveRes.data || []);
      } catch (err) {
        console.error('Error fetching leave requests:', err);
      }
      
      // Fetch holidays
      try {
        const holidayRes = await api.get('/holidays');
        setHolidays(holidayRes.data || []);
      } catch (err) {
        console.error('Error fetching holidays:', err);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProjectById = (id) => projects.find(p => p.id === id);

  // Always show hours + minutes format (e.g., "0h 1m", "2h 35m")
  const formatTimeOnly = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const formatDateRange = () => {
    if (viewMode === 'week') {
      return `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`;
    } else if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    }
  };

  const navigatePrevious = () => {
    if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const navigateNext = () => {
    if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get entries for a specific day - include all entries, even very short ones
  const getEntriesForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return entries.filter(e => e.start_time?.slice(0, 10) === dateStr);
  };

  // Get total hours for a day - use summary API data when available (includes manual timesheet entries)
  const getDayTotal = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Check if we have summary daily breakdown for accurate totals
    if (weeklySummary?.daily_breakdown) {
      const dayData = weeklySummary.daily_breakdown.find(d => d.date === dateStr);
      if (dayData) {
        return dayData.duration || 0;
      }
    }
    
    // Fallback to local calculation from timer entries
    return getEntriesForDay(date).reduce((acc, e) => acc + (e.duration || 0), 0);
  };

  // Get unvisualized hours for a day (manual timesheet hours that can't be shown as blocks)
  // This is the difference between summary total and timer entries total
  const getUnvisualizedHours = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const summaryDayTotal = weeklySummary?.daily_breakdown?.find(d => d.date === dateStr)?.duration || 0;
    const timerEntriesTotal = getEntriesForDay(date).reduce((acc, e) => acc + (e.duration || 0), 0);
    return Math.max(0, summaryDayTotal - timerEntriesTotal);
  };

  // Week days for week view
  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return [];
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  }, [viewMode, dateRange]);

  // Projects with entries in current view
  const projectsInView = useMemo(() => {
    const projectIds = new Set();
    entries.forEach(e => {
      if (e.project_id) projectIds.add(e.project_id);
    });
    return projects.filter(p => projectIds.has(p.id));
  }, [entries, projects]);

  // Weekly totals - use summary API data for accurate total (includes manual timesheet entries)
  const weeklyTotal = useMemo(() => {
    // Prefer summary API data as it merges timer + manual entries
    if (weeklySummary?.total_duration !== undefined) {
      return weeklySummary.total_duration;
    }
    // Fallback to local calculation if summary not available
    return entries.reduce((acc, e) => acc + (e.duration || 0), 0);
  }, [weeklySummary, entries]);

  const lastWeekTotal = useMemo(() => {
    return lastWeekEntries.reduce((acc, e) => acc + (e.duration || 0), 0);
  }, [lastWeekEntries]);

  // Project breakdown for sidebar - use summary API data for accurate totals
  const projectBreakdown = useMemo(() => {
    // Prefer summary API data as it merges timer + manual entries
    if (weeklySummary?.project_breakdown?.length) {
      return weeklySummary.project_breakdown.map(p => ({
        project: getProjectById(p.project_id) || { id: p.project_id || 'no-project', name: p.project_name || 'No Project', color: p.color || '#94a3b8' },
        duration: p.duration
      })).sort((a, b) => b.duration - a.duration);
    }
    
    // Fallback to local calculation if summary not available
    const breakdown = {};
    entries.forEach(e => {
      const projectId = e.project_id || 'no-project';
      breakdown[projectId] = (breakdown[projectId] || 0) + (e.duration || 0);
    });
    return Object.entries(breakdown)
      .map(([id, duration]) => ({
        project: getProjectById(id) || { id: 'no-project', name: 'No Project', color: '#94a3b8' },
        duration
      }))
      .sort((a, b) => b.duration - a.duration);
  }, [weeklySummary, entries, projects]);

  // Calculate entry position in timeline
  const getEntryPosition = (entry) => {
    const startTime = new Date(entry.start_time);
    const startHour = getHours(startTime) + getMinutes(startTime) / 60;
    const top = Math.max(0, (startHour - HOURS_START) * HOUR_HEIGHT);
    // Minimum height of 20px for visibility, even for very short entries
    const calculatedHeight = (entry.duration / 3600) * HOUR_HEIGHT;
    const height = Math.max(20, calculatedHeight);
    return { top, height };
  };

  // Check if a day is a weekend (Saturday=6, Sunday=0)
  const isWeekend = (date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  // Get holiday for a specific date
  const getHolidayForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.find(h => h.date === dateStr);
  };

  // Get approved leave for a specific date
  const getApprovedLeaveForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return leaveRequests.find(r => 
      r.status === 'approved' && 
      dateStr >= r.start_date?.slice(0, 10) && 
      dateStr <= r.end_date?.slice(0, 10)
    );
  };

  // Get pending leave for a specific date
  const getPendingLeaveForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return leaveRequests.find(r => 
      r.status === 'pending' && 
      dateStr >= r.start_date?.slice(0, 10) && 
      dateStr <= r.end_date?.slice(0, 10)
    );
  };

  // Check if day is overdue (past working day with no entries)
  const isDayOverdue = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    // Not overdue if: today, future, weekend, holiday, or has approved leave
    if (checkDate >= today) return false;
    if (isWeekend(date)) return false;
    if (getHolidayForDay(date)) return false;
    if (getApprovedLeaveForDay(date)) return false;
    
    // Overdue if past working day with zero hours
    const dayTotal = getDayTotal(date);
    return dayTotal === 0;
  };

  // Check if week has any entries
  const weekHasEntries = useMemo(() => {
    return entries.length > 0;
  }, [entries]);

  // Truncate project name with tooltip support
  const truncateProjectName = (name, maxLength = 25) => {
    if (!name) return 'No Project';
    if (name.length <= maxLength) return name;
    return name.slice(0, maxLength) + '...';
  };

  // Handle clicking empty slot - opens manual entry modal with pre-filled date/time
  const handleSlotClick = (date, hour, minutes = 0) => {
    const startTime = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    const endMinutes = minutes + 60;
    const endHour = hour + Math.floor(endMinutes / 60);
    const endMin = endMinutes % 60;
    const finalEndHour = Math.min(endHour, HOURS_END);
    const endTime = `${finalEndHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
    
    setEntryForm({
      description: '',
      project_id: '',
      date: date,
      start_time: startTime,
      end_time: endTime
    });
    setEditingEntry(null);
    setShowEntryModal(true);
  };

  // Handle clicking entry to edit
  const handleEntryClick = (entry, e) => {
    e.stopPropagation();
    setHoveredEntry(null); // Close tooltip when opening edit dialog
    const startTime = formatTimeOnly(entry.start_time);
    const endTime = entry.end_time ? formatTimeOnly(entry.end_time) : '17:00';
    
    setEntryForm({
      description: entry.description || '',
      project_id: entry.project_id || '',
      date: new Date(entry.start_time),
      start_time: startTime,
      end_time: endTime
    });
    setEditingEntry(entry);
    setShowEntryModal(true);
  };

  // Handle tooltip
  const handleEntryHover = (entry, e) => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setHoveredEntry(entry);
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }, 300);
  };

  const handleEntryLeave = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setHoveredEntry(null);
  };

  // Save entry
  const handleSaveEntry = async () => {
    // Validate end time > start time
    const [startH, startM] = entryForm.start_time.split(':').map(Number);
    const [endH, endM] = entryForm.end_time.split(':').map(Number);
    if (endH * 60 + endM <= startH * 60 + startM) {
      toast.error('End time must be after start time');
      return;
    }
    
    try {
      const dateStr = format(entryForm.date, 'yyyy-MM-dd');
      const startDateTime = `${dateStr}T${entryForm.start_time}:00.000Z`;
      const endDateTime = `${dateStr}T${entryForm.end_time}:00.000Z`;
      
      if (editingEntry) {
        await api.put(`/time-entries/${editingEntry.id}`, {
          description: entryForm.description,
          project_id: entryForm.project_id || null,
          start_time: startDateTime,
          end_time: endDateTime
        });
        toast.success('Entry updated');
      } else {
        await api.post('/time-entries', {
          description: entryForm.description,
          project_id: entryForm.project_id || null,
          start_time: startDateTime,
          end_time: endDateTime
        });
        toast.success('Entry added');
      }
      
      setShowEntryModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save entry');
    }
  };

  // Continue timer from entry
  const handleContinueEntry = (entry) => {
    startTimer({ 
      project_id: entry.project_id, 
      description: entry.description 
    });
    toast.success('Timer started');
  };

  // Filtered projects for dropdown
  const filteredProjects = useMemo(() => {
    if (!projectSearch) return projects;
    return projects.filter(p => 
      p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
      p.client_name?.toLowerCase().includes(projectSearch.toLowerCase())
    );
  }, [projects, projectSearch]);

  // Current time position for indicator
  const currentTimePosition = useMemo(() => {
    const now = new Date();
    const hour = getHours(now) + getMinutes(now) / 60;
    if (hour < HOURS_START || hour > HOURS_END) return null;
    return (hour - HOURS_START) * HOUR_HEIGHT;
  }, []);

  // Render week view
  const renderWeekView = () => (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Day headers */}
      <div className="flex border-b border-slate-200 dark:border-slate-700">
        <div className="w-16 flex-shrink-0" /> {/* Time column spacer */}
        {weekDays.map((day, idx) => {
          const dayTotal = getDayTotal(day);
          const holiday = getHolidayForDay(day);
          const approvedLeave = getApprovedLeaveForDay(day);
          const pendingLeave = getPendingLeaveForDay(day);
          const overdue = isDayOverdue(day);
          const weekend = isWeekend(day);
          const isFutureOrToday = !isBefore(day, new Date()) || isToday(day);
          
          return (
            <div 
              key={idx}
              className={`flex-1 text-center border-l border-slate-200 dark:border-slate-700 first:border-l-0 ${
                isToday(day) ? 'bg-blue-50 dark:bg-blue-900/10' : ''
              } ${holiday ? 'bg-red-50 dark:bg-red-900/30/50 dark:bg-red-900/5' : ''}`}
            >
              {/* Leave banner - above date header */}
              {(approvedLeave || pendingLeave) && (
                <div className={`px-2 py-1 text-[10px] font-medium truncate ${
                  approvedLeave 
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' 
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border-b border-dashed border-amber-300'
                }`}>
                  {approvedLeave?.leave_type || pendingLeave?.leave_type || 'Leave'}
                  {pendingLeave && ' (Pending)'}
                </div>
              )}
              
              <div className="p-3">
                <div className={`text-xs font-medium ${isToday(day) ? 'text-blue-600' : 'text-slate-500'}`}>
                  {format(day, 'EEE')}
                </div>
                <div className={`text-lg font-semibold ${isToday(day) ? 'text-blue-600' : 'text-slate-900 dark:text-white'}`}>
                  {format(day, 'd')}
                </div>
                
                {/* Holiday label */}
                {holiday && (
                  <div className="text-[10px] text-red-600 dark:text-red-400 font-medium truncate mt-0.5">
                    {holiday.name}
                  </div>
                )}
                
                {/* Duration - color based on status */}
                <div className={`text-xs font-medium mt-1 ${
                  overdue 
                    ? 'text-amber-600 dark:text-amber-400' 
                    : dayTotal === 0 
                      ? 'text-slate-300 dark:text-slate-600' 
                      : 'text-slate-500'
                }`}>
                  {formatDuration(dayTotal)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Timeline */}
      <div ref={timelineRef} className="flex-1 overflow-y-auto relative">
        {/* Empty state message when no entries */}
        {!weekHasEntries && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="text-center px-6 py-4 bg-white dark:bg-slate-800/80 dark:bg-slate-900/80 rounded-xl">
              <CalendarDays className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-400 dark:text-slate-500">
                No time entries this week.
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Time entries from your Time Tracker will appear here as blocks.
              </p>
            </div>
          </div>
        )}
        
        <div className="flex" style={{ minHeight: (HOURS_END - HOURS_START) * HOUR_HEIGHT }}>
          {/* Time labels */}
          <div className="w-16 flex-shrink-0 relative">
            {Array.from({ length: HOURS_END - HOURS_START + 1 }, (_, i) => i + HOURS_START).map(hour => (
              <div 
                key={hour}
                className="absolute left-0 right-0 text-xs text-slate-400 text-right pr-2"
                style={{ top: (hour - HOURS_START) * HOUR_HEIGHT - 6 }}
              >
                {`${hour.toString().padStart(2, '0')}:00`}
              </div>
            ))}
          </div>
          
          {/* Day columns */}
          {weekDays.map((day, dayIdx) => {
            const holiday = getHolidayForDay(day);
            const isHolidayOrLeave = holiday || getApprovedLeaveForDay(day);
            
            return (
              <div 
                key={dayIdx}
                className={`flex-1 relative border-l border-slate-200 dark:border-slate-700 ${
                  holiday ? 'bg-slate-50 dark:bg-slate-700/50 dark:bg-slate-800/30' : ''
                }`}
              >
                {/* Hour grid lines - clickable slots */}
                {Array.from({ length: HOURS_END - HOURS_START + 1 }, (_, i) => i + HOURS_START).map(hour => (
                  <div 
                    key={hour}
                    className={`absolute left-0 right-0 border-t border-slate-100 dark:border-slate-800 cursor-pointer transition-colors ${
                      holiday 
                        ? 'bg-slate-50 dark:bg-slate-700/50 dark:bg-slate-800/30' 
                        : 'hover:bg-blue-50 dark:bg-blue-900/30/50 dark:hover:bg-blue-50 dark:bg-blue-900/5'
                    }`}
                    style={{ top: (hour - HOURS_START) * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                    data-testid={`calendar-slot-${format(day, 'yyyy-MM-dd')}-${hour}`}
                  />
                ))}
                
                {/* Current time indicator */}
                {isToday(day) && currentTimePosition !== null && (
                  <div 
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: currentTimePosition }}
                  >
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-red-50 dark:bg-red-900/30" />
                      <div className="flex-1 h-0.5 bg-red-50 dark:bg-red-900/30" />
                    </div>
                  </div>
                )}
                
                {/* Manual timesheet hours indicator - shows hours entered on Timesheet page that can't be visualized as blocks */}
                {(() => {
                  const unvisualizedHours = getUnvisualizedHours(day);
                  if (unvisualizedHours <= 0) return null;
                  
                  // Calculate bar height based on actual hours (1 hour = HOUR_HEIGHT pixels)
                  const hoursWorked = unvisualizedHours / 3600;
                  const barHeight = hoursWorked * HOUR_HEIGHT;
                  
                  return (
                    <div 
                      className="absolute left-1 right-1 rounded-lg bg-gradient-to-b from-blue-100 to-blue-50 dark:from-blue-500/20 dark:to-blue-500/5 border border-dashed border-blue-300 dark:border-blue-500/30 z-5"
                      style={{ top: 4, height: Math.max(40, barHeight) }}
                      title={`${formatDuration(unvisualizedHours)} from timesheet (not from timer)`}
                    >
                      <div className="p-2 text-center">
                        <div className="text-[10px] font-medium text-blue-600 dark:text-blue-400">
                          Timesheet
                        </div>
                        <div className="text-xs font-bold text-blue-700 dark:text-blue-300">
                          {formatDuration(unvisualizedHours)}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                
                {/* Entry blocks with project colors */}
                {getEntriesForDay(day).map(entry => {
                  const project = getProjectById(entry.project_id);
                  const pos = getEntryPosition(entry);
                  return (
                    <div
                      key={entry.id}
                      className="absolute left-1 right-1 rounded-lg overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-shadow z-10"
                      style={{
                        top: pos.top,
                        height: pos.height,
                        backgroundColor: project?.color || '#94a3b8',
                        minHeight: 20
                      }}
                      onClick={(e) => handleEntryClick(entry, e)}
                      onMouseEnter={(e) => handleEntryHover(entry, e)}
                      onMouseLeave={handleEntryLeave}
                      data-testid={`entry-block-${entry.id}`}
                    >
                      <div className="p-1 text-white h-full overflow-hidden flex flex-col justify-center">
                        {pos.height > 50 && (
                          <>
                            <div className="text-[10px] font-semibold truncate leading-tight opacity-90">
                              {project?.name || 'No Project'}
                            </div>
                            <div className="text-xs font-medium truncate leading-tight mt-0.5">
                              {entry.description || <span className="italic opacity-70">No description</span>}
                            </div>
                            <div className="text-[10px] opacity-80 leading-tight mt-0.5">
                              {formatDuration(entry.duration)}
                            </div>
                          </>
                        )}
                        {pos.height <= 50 && pos.height > 30 && (
                          <>
                            <div className="text-[10px] font-medium truncate leading-tight">
                              {project?.name || 'No Project'}
                            </div>
                            <div className="text-[9px] opacity-80 leading-tight">
                              {formatDuration(entry.duration)}
                            </div>
                          </>
                        )}
                        {pos.height <= 30 && (
                          <div className="text-[9px] font-medium truncate leading-none text-center">
                            {formatDuration(entry.duration)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Render month view
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    
    return (
      <div className="flex-1 overflow-auto">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
            <div key={day} className="p-2 text-center text-xs font-medium text-slate-500">
              {day}
            </div>
          ))}
        </div>
        
        {/* Weeks */}
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
            {week.map((day, dayIdx) => {
              const dayEntries = getEntriesForDay(day);
              const dayTotal = getDayTotal(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              
              return (
                <div 
                  key={dayIdx}
                  className={`min-h-[100px] p-2 border-l border-slate-100 dark:border-slate-800 first:border-l-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                    !isCurrentMonth ? 'bg-slate-50 dark:bg-slate-700/50 dark:bg-slate-900/50' : ''
                  } ${isToday(day) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                  onClick={() => {
                    setCurrentDate(day);
                    setViewMode('day');
                  }}
                >
                  <div className={`text-sm font-medium ${
                    isToday(day) ? 'text-blue-600' : isCurrentMonth ? 'text-slate-900 dark:text-white' : 'text-slate-400'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  {dayTotal > 0 && (
                    <div className="text-xs text-slate-500 mt-1">
                      {formatDuration(dayTotal)}
                    </div>
                  )}
                  <div className="mt-1 space-y-0.5">
                    {dayEntries.slice(0, 3).map(entry => {
                      const project = getProjectById(entry.project_id);
                      return (
                        <div 
                          key={entry.id}
                          className="h-1.5 rounded-full"
                          style={{ backgroundColor: project?.color || '#94a3b8' }}
                        />
                      );
                    })}
                    {dayEntries.length > 3 && (
                      <div className="text-[10px] text-slate-400">
                        +{dayEntries.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Render day view
  const renderDayView = () => (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* Day header */}
      <div className={`p-4 text-center border-b border-slate-200 dark:border-slate-700 ${
        isToday(currentDate) ? 'bg-blue-50 dark:bg-blue-900/10' : ''
      }`}>
        <div className={`text-2xl font-bold ${isToday(currentDate) ? 'text-blue-600' : 'text-slate-900 dark:text-white'}`}>
          {format(currentDate, 'EEEE, MMMM d')}
        </div>
        <div className="text-lg font-medium text-slate-500 mt-1">
          {formatDuration(getDayTotal(currentDate))} tracked
        </div>
      </div>
      
      {/* Timeline */}
      <div ref={timelineRef} className="flex-1 overflow-y-auto relative">
        <div className="flex" style={{ minHeight: (HOURS_END - HOURS_START) * HOUR_HEIGHT }}>
          {/* Time labels */}
          <div className="w-20 flex-shrink-0 relative">
            {Array.from({ length: HOURS_END - HOURS_START + 1 }, (_, i) => i + HOURS_START).map(hour => (
              <div 
                key={hour}
                className="absolute left-0 right-0 text-sm text-slate-400 text-right pr-3"
                style={{ top: (hour - HOURS_START) * HOUR_HEIGHT - 8 }}
              >
                {`${hour.toString().padStart(2, '0')}:00`}
              </div>
            ))}
          </div>
          
          {/* Day column */}
          <div className="flex-1 relative border-l border-slate-200 dark:border-slate-700">
            {/* Hour grid lines */}
            {Array.from({ length: HOURS_END - HOURS_START + 1 }, (_, i) => i + HOURS_START).map(hour => (
              <div 
                key={hour}
                className="absolute left-0 right-0 border-t border-slate-100 dark:border-slate-800"
                style={{ top: (hour - HOURS_START) * HOUR_HEIGHT, height: HOUR_HEIGHT }}
              />
            ))}
            
            {/* Current time indicator */}
            {isToday(currentDate) && currentTimePosition !== null && (
              <div 
                className="absolute left-0 right-0 z-20 pointer-events-none"
                style={{ top: currentTimePosition }}
              >
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-red-50 dark:bg-red-900/30" />
                  <div className="flex-1 h-0.5 bg-red-50 dark:bg-red-900/30" />
                </div>
              </div>
            )}
            
            {/* Entry blocks */}
            {getEntriesForDay(currentDate).map(entry => {
              const project = getProjectById(entry.project_id);
              const pos = getEntryPosition(entry);
              return (
                <div
                  key={entry.id}
                  className="absolute left-2 right-2 rounded-xl overflow-hidden cursor-pointer shadow-md hover:shadow-lg transition-shadow z-10"
                  style={{
                    top: pos.top,
                    height: pos.height,
                    backgroundColor: project?.color || '#94a3b8',
                    minHeight: 40
                  }}
                  onClick={(e) => handleEntryClick(entry, e)}
                  onMouseEnter={(e) => handleEntryHover(entry, e)}
                  onMouseLeave={handleEntryLeave}
                >
                  <div className="p-3 text-white h-full">
                    <div className="font-medium">
                      {entry.description || <span className="italic opacity-70">No description added</span>}
                    </div>
                    <div className="text-sm opacity-80 mt-1">
                      {project?.name || 'No Project'} · {formatDuration(entry.duration)}
                    </div>
                    <div className="text-xs opacity-70 mt-1">
                      {formatTimeOnly(entry.start_time)} - {entry.end_time ? formatTimeOnly(entry.end_time) : 'Running'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {t('calendar.title')}
            </h1>
            <p className="text-slate-500 mt-1">{t('calendar.subtitle')}</p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle */}
            <div className="flex items-center p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
              {['month', 'week', 'day'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all capitalize ${
                    viewMode === mode
                      ? 'bg-white dark:bg-slate-700 text-blue-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`view-${mode}-btn`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={navigatePrevious}
              className="h-10 w-10"
              data-testid="prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={navigateNext}
              className="h-10 w-10"
              data-testid="next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
            <Button 
              variant="outline" 
              onClick={goToToday}
              className="h-10"
              data-testid="today-btn"
            >
              Today
            </Button>
          </div>
          
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            {formatDateRange()}
          </h2>
          
          <div className="text-sm text-slate-500">
            Use ← → arrow keys to navigate
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex gap-4 min-h-0">
          {/* Calendar */}
          <div className="flex-1 premium-card overflow-hidden flex flex-col" ref={calendarRef}>
            {viewMode === 'week' && renderWeekView()}
            {viewMode === 'month' && renderMonthView()}
            {viewMode === 'day' && renderDayView()}
          </div>
          
          {/* Sidebar - Week summary */}
          {viewMode === 'week' && (
            <div className="w-72 flex-shrink-0 space-y-4">
              {/* Weekly total */}
              <div className="premium-card p-5">
                <h3 className="text-sm font-medium text-slate-500 mb-2">This Week</h3>
                <div className="text-3xl font-bold text-slate-900 dark:text-white timer-display">
                  {formatDuration(weeklyTotal)}
                </div>
                {lastWeekTotal > 0 && (
                  <div className={`flex items-center gap-1 mt-2 text-sm ${
                    weeklyTotal >= lastWeekTotal ? 'text-emerald-600' : 'text-red-500'
                  }`}>
                    {weeklyTotal >= lastWeekTotal ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    <span>
                      {weeklyTotal >= lastWeekTotal ? '+' : '-'}
                      {formatDuration(Math.abs(weeklyTotal - lastWeekTotal))} vs last week
                    </span>
                  </div>
                )}
              </div>
              
              {/* Project breakdown with tooltips for long names */}
              <TooltipProvider>
                <div className="premium-card p-5">
                  <h3 className="text-sm font-medium text-slate-500 mb-4">By Project</h3>
                  <div className="space-y-3">
                    {projectBreakdown.slice(0, 6).map(({ project, duration }) => {
                      const percent = weeklyTotal ? Math.round((duration / weeklyTotal) * 100) : 0;
                      const isLongName = project.name && project.name.length > 25;
                      return (
                        <div key={project.id}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <div 
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: project.color }}
                              />
                              {isLongName ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate cursor-help">
                                      {truncateProjectName(project.name)}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{project.name}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">
                                  {project.name}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-slate-500 flex-shrink-0 ml-2">
                              {formatDuration(duration)}
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full"
                              style={{ width: `${percent}%`, backgroundColor: project.color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {projectBreakdown.length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-4">No entries this week</p>
                    )}
                  </div>
                </div>
              </TooltipProvider>
            </div>
          )}
        </div>

        {/* Entry tooltip */}
        {hoveredEntry && (
          <div 
            className="fixed z-50 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-100 dark:border-slate-700 p-4 max-w-xs"
            style={{ 
              left: Math.min(tooltipPosition.x + 10, window.innerWidth - 280),
              top: Math.min(tooltipPosition.y + 10, window.innerHeight - 200)
            }}
            onMouseLeave={() => setHoveredEntry(null)}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-medium text-slate-900 dark:text-white">
              {hoveredEntry.description || <span className="italic text-slate-400 dark:text-slate-500">No description added</span>}
            </div>
            <div className="text-sm text-slate-500 mt-1">
              {getProjectById(hoveredEntry.project_id)?.name || 'No Project'}
            </div>
            <div className="text-sm text-slate-500 mt-2">
              {formatTimeOnly(hoveredEntry.start_time)} - {hoveredEntry.end_time ? formatTimeOnly(hoveredEntry.end_time) : 'Running'}
            </div>
            <div className="text-lg font-semibold text-slate-900 dark:text-white mt-1 timer-display">
              {formatDuration(hoveredEntry.duration)}
            </div>
          </div>
        )}

        {/* Entry modal - only for editing existing entries */}
        <Dialog open={showEntryModal} onOpenChange={setShowEntryModal}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl">
                Edit Entry
              </DialogTitle>
              <DialogDescription>
                {format(entryForm.date, 'EEEE, MMMM d, yyyy')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="label-premium">Description</Label>
                <Input
                  placeholder="What are you working on?"
                  value={entryForm.description}
                  onChange={(e) => setEntryForm(prev => ({ ...prev, description: e.target.value }))}
                  className="input-premium"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-premium">Start Time</Label>
                  <Input
                    type="time"
                    value={entryForm.start_time}
                    onChange={(e) => setEntryForm(prev => ({ ...prev, start_time: e.target.value }))}
                    className="input-premium"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-premium">End Time</Label>
                  <Input
                    type="time"
                    value={entryForm.end_time}
                    onChange={(e) => setEntryForm(prev => ({ ...prev, end_time: e.target.value }))}
                    className="input-premium"
                  />
                </div>
              </div>
              
              {/* Duration display */}
              <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <span className="text-sm text-slate-500">Duration: </span>
                <span className="text-lg font-semibold text-slate-900 dark:text-white timer-display">
                  {(() => {
                    const [startH, startM] = entryForm.start_time.split(':').map(Number);
                    const [endH, endM] = entryForm.end_time.split(':').map(Number);
                    const mins = (endH * 60 + endM) - (startH * 60 + startM);
                    if (mins <= 0) return '0h 0m';
                    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
                  })()}
                </span>
              </div>
              
              {/* Project selector - Employee view (no create option) */}
              <div className="space-y-2">
                <Label className="label-premium">Project</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start h-11 rounded-xl">
                      {entryForm.project_id ? (
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: getProjectById(entryForm.project_id)?.color || '#94a3b8' }}
                          />
                          <span>{getProjectById(entryForm.project_id)?.name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-500">Select project</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search projects..."
                          value={projectSearch}
                          onChange={(e) => setProjectSearch(e.target.value)}
                          className="pl-8 h-9"
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto p-1">
                      {projects.length === 0 ? (
                        <div className="px-3 py-4 text-center">
                          <p className="text-sm text-slate-500">No projects assigned yet</p>
                          <p className="text-xs text-slate-400 mt-1">Contact your manager</p>
                        </div>
                      ) : (
                        <>
                          <button
                            className="w-full px-3 py-2 text-left rounded-lg hover:bg-slate-50 text-sm"
                            onClick={() => {
                              setEntryForm(prev => ({ ...prev, project_id: '' }));
                              setProjectSearch('');
                            }}
                          >
                            <span className="text-slate-500">No Project</span>
                          </button>
                          {filteredProjects.map(project => (
                            <button
                              key={project.id}
                              className="w-full px-3 py-2 text-left rounded-lg hover:bg-slate-50 flex items-center gap-2"
                              onClick={() => {
                                setEntryForm(prev => ({ ...prev, project_id: project.id }));
                                setProjectSearch('');
                              }}
                            >
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: project.color }}
                              />
                              <div>
                                <span className="text-sm font-medium">{project.name}</span>
                                {project.client_name && (
                                  <span className="text-xs text-slate-400 block">{project.client_name}</span>
                                )}
                              </div>
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setShowEntryModal(false)} className="rounded-xl">
                Cancel
              </Button>
              <Button onClick={handleSaveEntry} className="btn-premium rounded-xl">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CalendarView;
